'use client'

import { captureEvent, captureException } from '@sentry/nextjs'
import { useRouter } from 'next/router'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { z } from 'zod'

import { logger } from 'utils/logger'
import { checkErrorInstance } from 'utils/check-error-instance'

const stringOrStringsToOptionalStringSchema = z
  .union([z.string(), z.array(z.string())])
  .transform((value): string | undefined => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        const string = value[0]

        if (string.length > 0) {
          return string
        }
      }
    } else {
      if (value.length > 0) {
        return value
      }
    }

    return undefined
  })

const fetchedGeoLocationDataSchema = z.object({
  country: stringOrStringsToOptionalStringSchema.optional(),
  state: stringOrStringsToOptionalStringSchema.optional(),
  city: stringOrStringsToOptionalStringSchema.optional(),
})

type ClientGeoLocationStatus = '' | 'done'

type ClientGeoLocationData = {
  country?: string
  state?: string
  city?: string
}

const CLIENT_GEO_LOCATION_INITIAL_STATUS =
  '' as const satisfies ClientGeoLocationStatus

const EMPTY_CLIENT_GEO_LOCATION_DATA =
  {} as const satisfies ClientGeoLocationData

const ClientGeoLocationContext = createContext<
  ClientGeoLocationData & { status: ClientGeoLocationStatus }
>({
  ...EMPTY_CLIENT_GEO_LOCATION_DATA,
  status: CLIENT_GEO_LOCATION_INITIAL_STATUS,
})

const IS_INTERNATIONAL_USER_LOCAL_STORAGE_KEY = 'isInternationalUser' as const

const LEGAL_PAGE_PATHS = [
  '/privacy/',
  '/terms-conditions/',
  '/partners/',
  '/ratings/',
  '/e-sign-agreement/',
  '/dividend-terms/',
  '/credit-authorization-agreement/',
  '/awards-methodology/',
] as const

const PATHS_OF_INTERNATIONALLY_ACCESSIBLE_PAGES: readonly string[] = [
  ...LEGAL_PAGE_PATHS,
  '/international/',
] as const

/**
 * @todo: This component could use some improvements, such as:
 * - Instead of calling an API route to get location data, which adds an
 *   unnecessary round trip network request (which in this case blocks all data
 *   fetching and rendering below this component in the React component tree),
 *   use middleware to set this information in browser cookies, which can then
 *   be accessed immediately on the client side.
 * - Refactor to always render context provider and children (not doing so until
 *   location data is available blocks all data fetching and rendering below
 *   this component in the React component tree until the API request for
 *   location data completes).
 * - Implement the context in such a way as to differentiate between cases where
 *   geo location data is unavailable because the consumer has not been wrapped
 *   in this provider, which would be a mistake in our code which we would want
 *   to throw an error to help us catch early, vs cases where geo location data
 *   is unavailable for other reasons. Could be done e.x. by using a Symbol as
 *   the initial value. Then implement the access hook to throw an error if it
 *   is called by a consumer not wrapped in this provider (e.x. by checking if
 *   the context value matches that Symbol).
 * - Possibly implement international user detection and handling more
 *   consistently. Currently, the first page load's redirection logic handles
 *   the case where the location fetch fails differently from the case where it
 *   succeeds but doesn't contain country info, which may not be intended.
 *   Additionally, the redirection logic differs between the initial page load
 *   and subsequent navigation attempts if the location fetch fails. As an
 *   example, if the location fetch fails for a user whose localStorage value
 *   (from a previous session) is set to international, they will be able to see
 *   the first page they visit (page load redirection logic), but any attempts
 *   to use the navigation will result in redirection to the /international page
 *   (navigation redirection logic).
 */
export const ClientGeoLocationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const router = useRouter()

  const [status, setStatus] = useState<ClientGeoLocationStatus>(
    CLIENT_GEO_LOCATION_INITIAL_STATUS,
  )

  const [clientGeoLocationData, setClientGeoLocationData] =
    useState<ClientGeoLocationData>(EMPTY_CLIENT_GEO_LOCATION_DATA)

  const { country, state: state } = clientGeoLocationData

  /**
   * Redirects user to /international page if user is international and not on a
   * page that international users are allowed to access.
   */
  const conditionallyRedirectToInternationalPage = useCallback(
    ({
      isInternationalUser,
      currentPagePath,
      country: countryParam,
      state: stateParam,
    }: {
      isInternationalUser: boolean
      currentPagePath: string
      country?: ClientGeoLocationData['country']
      state?: ClientGeoLocationData['state']
    }): void => {
      const isInternationallyAccessiblePage =
        PATHS_OF_INTERNATIONALLY_ACCESSIBLE_PAGES.includes(currentPagePath)

      const shouldRedirectToInternationalPage =
        isInternationalUser && !isInternationallyAccessiblePage

      if (shouldRedirectToInternationalPage) {
        captureEvent({
          level: 'info',
          message: 'Client routed to /international',
          extra: {
            country: countryParam || country || undefined,
            state: stateParam || state || undefined,
          },
        })
        router.push('/international')
      }
    },
    [country, state, router],
  )

  /**
   * On page load, get the user's geographic location from the server, store in
   * localStorage whether the user is international, and redirect them to the
   * /international page if they are an international user and not on a page
   * that international users are allowed to access.
   */
  useEffect(() => {
    const fetchLocationAndHandleResponse = async () => {
      try {
        const locationResponse = await fetch('/api/get-location')

        if (locationResponse.ok) {
          const fetchedGeoLocationDataUnparsed: unknown =
            await locationResponse.json()

          const fetchedGeoLocationData = fetchedGeoLocationDataSchema.parse(
            fetchedGeoLocationDataUnparsed,
          )

          const fetchedGeolocationIsInternational =
            !!fetchedGeoLocationData.country &&
            fetchedGeoLocationData.country !== 'US'

          // Used later to redirect interntional users during route changes
          localStorage.setItem(
            IS_INTERNATIONAL_USER_LOCAL_STORAGE_KEY,
            `${fetchedGeolocationIsInternational}`,
          )

          const currentPagePath = new URL(window.location.href).pathname

          conditionallyRedirectToInternationalPage({
            isInternationalUser: fetchedGeolocationIsInternational,
            currentPagePath,
            country: fetchedGeoLocationData.country,
            state: fetchedGeoLocationData.state,
          })

          setClientGeoLocationData(fetchedGeoLocationData)
        } else if (locationResponse.status === 400) {
          // A response with status code 400 means the server was not able to
          // get the user's location, but there were no errors, so we just log
          // a message about this if logging is enabled.
          if (logger.getIsLogFunctionEnabled('log')) {
            const responseJson: unknown = await locationResponse.json()

            const serverMessage =
              typeof responseJson === 'object' &&
              !!responseJson &&
              'message' in responseJson &&
              typeof responseJson.message === 'string'
                ? responseJson.message
                : null

            logger.log(
              `Server was unable to get client's geographic location${
                serverMessage
                  ? `. Server response message: ${serverMessage}`
                  : ''
              }`,
            )
            setStatus('done')
          }
        } else {
          throw new Error(
            'Unable to get client location: Response from get-location API request was not OK',
          )
        }
      } catch (originalError) {
        const error = checkErrorInstance(originalError)

        logger.error(error)
        if (process.env.DEPLOYMENT_TARGET === 'production') {
          captureException(error)
        }
      } finally {
        setStatus('done')
      }
    }

    fetchLocationAndHandleResponse()
  }, [])

  /**
   * Add listener to redirect international users to /international page on
   * route change if the page they are navigating to is not one of the pages
   * that international users are allowed to access.
   */
  useEffect(() => {
    const handleRouteChangeStart = (newPagePath: string) => {
      const isInternationalUser =
        localStorage.getItem(IS_INTERNATIONAL_USER_LOCAL_STORAGE_KEY) === 'true'

      conditionallyRedirectToInternationalPage({
        isInternationalUser,
        currentPagePath: newPagePath,
      })
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [router])

  const contextValue = useMemo(
    () => ({ ...clientGeoLocationData, status }),
    [clientGeoLocationData, status],
  )

  return status === 'done' ? (
    <ClientGeoLocationContext.Provider value={contextValue}>
      {children}
    </ClientGeoLocationContext.Provider>
  ) : null
}

export const useClientGeoLocation = () => useContext(ClientGeoLocationContext)
